import { useMemo } from "react";
import { useQuery } from "@apollo/client/react";
import { CustomFieldsListDataDocument } from "#app/lib/data";
import { getFieldTypeMeta } from "#app/lib/constants/field-type-icons";
import type { EntityItemConfig, EntityPoolConfig, IconName } from "@salestrak/ui";
import type { Omittable } from "@salestrak/ui";

const POOL_ID = "custom-fields";
const QUERY_VARS = { limit: 200 };

export type UseCustomFieldPoolOptions = Omittable<{
  /** Custom field IDs to exclude from the pool */
  exclude?: string[];
  /** Field TYPES to exclude (e.g. types an automation can't set) */
  excludeTypes?: readonly string[];
}>;

/**
 * Fetches workspace custom fields and returns an EntityPoolConfig.
 * Icons and colors are derived from the field type via FIELD_TYPE_META.
 */
export function useCustomFieldPool(options?: UseCustomFieldPoolOptions) {
  const { exclude, excludeTypes } = options ?? {};

  const { data, loading } = useQuery(CustomFieldsListDataDocument, {
    variables: QUERY_VARS,
  });

  const excludeSet = useMemo(
    () => (exclude?.length ? new Set(exclude) : null),
    [exclude],
  );
  const excludeTypeSet = useMemo(
    () => (excludeTypes?.length ? new Set(excludeTypes) : null),
    [excludeTypes],
  );

  const items = useMemo<EntityItemConfig[]>(() => {
    if (!data?.customFields?.edges) return [];
    const mapped = data.customFields.edges
      .filter((edge) => edge?.node)
      .filter((edge) => !excludeTypeSet?.has(edge.node.type))
      .map((edge) => {
        const node = edge.node;
        const meta = getFieldTypeMeta(node.type);
        return {
          id: node.customFieldId,
          title: node.name,
          avatar: {
            icon: meta.icon as IconName,
            color: meta.color,
            padded: true,
          },
          keywords: [node.type, meta.label, node.key].filter(Boolean) as string[],
          data: { type: node.type, key: node.key },
        } satisfies EntityItemConfig;
      });
    return excludeSet ? mapped.filter((item) => !excludeSet.has(item.id)) : mapped;
  }, [data, excludeSet, excludeTypeSet]);

  const pool = useMemo<EntityPoolConfig>(
    () => ({
      id: POOL_ID,
      label: "Custom Fields",
      items,
    }),
    [items],
  );

  return { pool, loading };
}

export { POOL_ID as CUSTOM_FIELD_POOL_ID };
